(() => {
  if (window.alegremeStoreInitialized) return;
  window.alegremeStoreInitialized = true;

  let cartRequest;
  const getCart = () => {
    if (!cartRequest) {
      const cartUrl = window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`;
      cartRequest = fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then(response => {
          if (!response.ok) throw new Error('cart_unavailable');
          return response.json();
        })
        .finally(() => { cartRequest = undefined; });
    }
    return cartRequest;
  };

  class AlegremeCartHelp extends HTMLElement {
    connectedCallback() {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
          clearTimeout(this.refreshTimer);
          this.refreshTimer = setTimeout(() => {
            if (!this.isConnected) return;
            getCart().then(cart => {
              if (this.isConnected) this.update(cart);
            }).catch(() => {});
          }, 80);
        });
      }
    }

    disconnectedCallback() {
      this.unsubscribe?.();
      clearTimeout(this.refreshTimer);
    }

    update(cart) {
      this.hidden = cart.item_count === 0;
      const shipping = this.querySelector('.alegreme-cart-help__shipping');
      if (shipping) {
        shipping.hidden = cart.currency !== 'EUR' || !cart.requires_shipping;
        const threshold = Number(this.dataset.threshold) || 5000;
        const total = Number(cart.total_price) || 0;
        const remaining = Math.max(0, threshold - total);
        const amount = new Intl.NumberFormat(document.documentElement.lang || 'pt-PT', { style: 'currency', currency: 'EUR' }).format(remaining / 100);
        this.querySelector('[data-shipping-status]').textContent = remaining > 0
          ? this.dataset.remaining.replace('[amount]', amount)
          : this.dataset.reached;
        this.querySelector('progress').value = Math.min(100, Math.max(0, total / threshold * 100));
      }
      const productIds = new Set(cart.items.map(item => String(item.product_id)));
      let visibleCount = 0;
      this.querySelectorAll('[data-suggestion]').forEach(item => {
        item.hidden = productIds.has(item.dataset.productId);
        if (!item.hidden) visibleCount += 1;
      });
      const suggestions = this.querySelector('.alegreme-cart-help__suggestions');
      if (suggestions) suggestions.hidden = visibleCount === 0;
    }
  }

  if (!customElements.get('alegreme-cart-help')) customElements.define('alegreme-cart-help', AlegremeCartHelp);

  const ga4Events = {
    homepage_click: 'alegreme_homepage_click',
    homepage_add_to_cart_click: 'alegreme_homepage_cart_intent',
    cart_suggestion_click: 'alegreme_cart_suggestion_click'
  };
  const allowedSurfaces = new Set(['hero_picantes', 'mais_vendidos_2026', 'outono_2026', 'guia_picantes', 'receitas', 'drawer', 'page', 'cart']);
  const productHandles = new Set([
    'sal-sado-1kg', 'psyllium-po-200g', 'goma-xantana-bio-200g',
    'extrato-baunilha-bourbon-madagascar-bio-100ml', 'molho-picante-carolina-reaper-bio-50ml',
    'canela-ceilao-po-bio-200', 'maca-bio-200g', 'cha-cbd-bio-40g',
    'sementes-chia-bio-350g', 'pumpkin-spice-latte-200g', 'manteiga-amendoim-350g',
    'tahini-branco-bio-350g', 'caju-bio-450g'
  ]);
  const destinationPaths = new Set([
    '/collections/molhos-picantes', '/collections/mais-vendidos-de-2026',
    '/pages/receitas-de-outono', '/pages/guia-de-picantes', '/pages/receitas-alegreme',
    ...Array.from(productHandles, handle => `/products/${handle}`)
  ]);

  const publishGoogle = (eventName, data) => {
    if (typeof window.gtag !== 'function' || !ga4Events[eventName] || !allowedSurfaces.has(data.surface)) return;
    const payload = {
      send_to: 'G-F2HZZFCN6Q',
      homepage_version: '2026-10-18',
      surface: data.surface,
      page_location: eventName === 'cart_suggestion_click' ? 'https://alegreme.pt/cart' : 'https://alegreme.pt/',
      page_referrer: '',
      page_title: 'Alegreme'
    };
    if (eventName === 'homepage_click') {
      const path = String(data.destination_path || '').replace(/^\/(?:pt(?:-pt)?|en|es)(?=\/)/i, '');
      if (!destinationPaths.has(path)) return;
      payload.destination_path = path;
    } else if (eventName === 'cart_suggestion_click') {
      if (!productHandles.has(data.product_handle)) return;
      payload.product_handle = data.product_handle;
    } else {
      payload.interaction = 'intent';
    }
    window.gtag('event', ga4Events[eventName], payload);
  };

  const publish = (eventName, data) => {
    if (window.Shopify?.designMode) return;
    try {
      if (window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() !== true) return;
    } catch (_) { return; }
    try {
      const result = window.Shopify?.analytics?.publish?.(`alegreme:${eventName}`, {
        homepage_version: '2026-10-18',
        ...data
      });
      if (result?.catch) result.catch(() => {});
    } catch (_) {}
    try { publishGoogle(eventName, data); } catch (_) {}
  };

  const getSurface = element => {
    const explicit = element.closest('[data-alegreme-surface]');
    if (explicit) return explicit.dataset.alegremeSurface;
    const sectionId = element.closest('.shopify-section')?.id || '';
    if (sectionId.endsWith('__image_banner_with_featured_collection')) return 'hero_picantes';
    if (sectionId.endsWith('__featured_collection_WmneLq')) return 'mais_vendidos_2026';
    return '';
  };

  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const target = event.target.closest('a[href], button');
    if (!target) return;
    const explicitEvent = target.dataset.alegremeEvent;
    const surface = getSurface(target);
    if (!surface && !explicitEvent) return;
    if (explicitEvent === 'cart_suggestion_click') {
      publish('cart_suggestion_click', {
        surface: target.closest('alegreme-cart-help')?.dataset.surface || 'cart',
        product_handle: target.dataset.productHandle || ''
      });
      return;
    }
    if (!surface) return;
    if (target.tagName === 'A') {
      let destination;
      try { destination = new URL(target.href, window.location.origin); } catch (_) { return; }
      if (destination.origin !== window.location.origin) return;
      if (!/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?(?:products|collections|pages|blogs)\//i.test(destination.pathname)) return;
      publish('homepage_click', { surface, destination_path: destination.pathname });
    } else if (target.matches('[type="submit"], .quick-add__submit, [data-alegreme-event="add_to_cart"]')) {
      publish('homepage_add_to_cart_click', { surface, interaction: 'intent' });
    }
  }, { passive: true });

  const ingredientItems = form => {
    const fields = new FormData(form);
    const items = new Map();
    for (const [name, value] of fields.entries()) {
      const match = /^items\[(\d+)\]\[(id|quantity)\]$/.exec(name);
      if (!match) continue;
      if (!items.has(match[1])) items.set(match[1], {});
      items.get(match[1])[match[2]] = String(value);
    }
    if (!items.size && fields.has('id')) {
      items.set('0', { id: String(fields.get('id')), quantity: String(fields.get('quantity') || '1') });
    }
    const result = Array.from(items.values(), item => ({ id: Number(item.id), quantity: Number(item.quantity || 1) }));
    if (!result.length || result.length > 20 || result.some(item =>
      !Number.isSafeInteger(item.id) || item.id <= 0 || !Number.isSafeInteger(item.quantity) || item.quantity < 1 || item.quantity > 99
    )) throw new Error('invalid_ingredient_items');
    return result;
  };

  document.addEventListener('submit', async event => {
    const form = event.target;
    if (!(form instanceof Element) || !form.matches('form[data-alegreme-ingredients]')) return;
    event.preventDefault();
    if (form.dataset.alegremePending === 'true' || form.dataset.alegremeReviewRequired === 'true') return;
    const status = form.querySelector('[data-alegreme-ingredients-status], [data-alegreme-status]');
    if (!status) return;
    const buttons = Array.from(form.querySelectorAll('button[type="submit"], button:not([type]), input[type="submit"]'));
    const originalDisabled = buttons.map(button => button.disabled);
    let requestStarted = false;
    let succeeded = false;
    form.dataset.alegremePending = 'true';
    form.setAttribute('aria-busy', 'true');
    buttons.forEach(button => { button.disabled = true; });
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.textContent = form.dataset.alegremePendingLabel || '';
    try {
      const items = ingredientItems(form);
      const rawRoot = window.Shopify?.routes?.root || '/';
      const root = rawRoot.endsWith('/') ? rawRoot : `${rawRoot}/`;
      const addUrl = new URL(`${root}cart/add.js`, window.location.origin);
      const cartUrl = new URL(`${root}cart`, window.location.origin);
      if (addUrl.origin !== window.location.origin || cartUrl.origin !== window.location.origin) throw new Error('invalid_cart_origin');
      requestStarted = true;
      const response = await fetch(addUrl.pathname, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items })
      });
      if (!response.ok) throw new Error('ingredients_not_added');
      succeeded = true;
      status.textContent = form.dataset.alegremeSuccessLabel || '';
      window.location.assign(cartUrl.pathname);
    } catch (_) {
      status.setAttribute('role', 'alert');
      status.setAttribute('aria-live', 'assertive');
      status.textContent = form.dataset.alegremeErrorLabel || '';
      if (requestStarted) {
        form.dataset.alegremeReviewRequired = 'true';
        const link = document.createElement('a');
        const rawRoot = window.Shopify?.routes?.root || '/';
        const root = rawRoot.endsWith('/') ? rawRoot : `${rawRoot}/`;
        const cartUrl = new URL(`${root}cart`, window.location.origin);
        link.href = cartUrl.origin === window.location.origin ? cartUrl.pathname : '/cart';
        link.textContent = form.dataset.alegremeCartLabel || '';
        status.append(' ', link);
      }
    } finally {
      form.dataset.alegremePending = 'false';
      form.removeAttribute('aria-busy');
      if (!succeeded && !requestStarted) buttons.forEach((button, index) => { button.disabled = originalDisabled[index]; });
    }
  });

  document.querySelectorAll('form[data-alegreme-ingredients]').forEach(form => {
    try {
      ingredientItems(form);
      if (!form.querySelector('[data-alegreme-ingredients-status], [data-alegreme-status]')) return;
      form.querySelectorAll('[data-alegreme-requires-js]').forEach(button => { button.disabled = false; });
      if (form.hasAttribute('data-alegreme-requires-js')) {
        form.querySelectorAll('button[type="submit"], button:not([type]), input[type="submit"]').forEach(button => { button.disabled = false; });
      }
    } catch (_) {}
  });
})();
